import { useState, useEffect } from "react";
import { Menu, X, LogOut } from "lucide-react";

// Official SMARTECH logo (brain + book on black)
export const SMARTECH_LOGO_URL = "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/24go47ow_20251231_131530.png";

// Responsive vertical sidebar navigation.
// Props:
//   title: string (dashboard label)
//   subtitle: string (user code / role)
//   items: Array<{ key, label, icon, badge?, testid? }>
//   active: string
//   onSelect: (key) => void
//   onLogout?: () => void
//   accent?: color (for header dot)
export const Sidebar = ({ title = "SMARTECH PRO", subtitle = "", items = [], active, onSelect, onLogout, accent = "#2563EB" }) => {
  const [open, setOpen] = useState(false);

  // Close drawer on route change / when clicking nav
  useEffect(() => { setOpen(false); }, [active]);

  const handleSelect = (k) => {
    onSelect && onSelect(k);
    setOpen(false);
  };

  const navBody = (
    <>
      <div className="sidebar-brand">
        <img src={SMARTECH_LOGO_URL} alt="SMARTECH" className="sidebar-brand-logo" />
        <div>
          <div className="sidebar-brand-title">{title}</div>
          {subtitle && <div className="sidebar-brand-sub">{subtitle}</div>}
        </div>
      </div>

      <nav className="sidebar-nav" data-testid="sidebar-nav">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = active === it.key;
          return (
            <button
              key={it.key}
              className={`sidebar-link ${isActive ? "active" : ""}`}
              onClick={() => handleSelect(it.key)}
              data-testid={it.testid || `sidebar-${it.key}`}
            >
              {Icon ? <Icon size={16} /> : <span style={{ width: 16, display: "inline-block" }} />}
              <span className="sidebar-link-label">{it.label}</span>
              {it.badge !== undefined && it.badge !== null && it.badge !== 0 && (
                <span
                  className="sidebar-badge"
                  style={it.badgeColor ? { background: it.badgeColor, color: "white" } : undefined}
                >{it.badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      {onLogout && (
        <div className="sidebar-footer">
          <button className="sidebar-link sidebar-logout" onClick={onLogout} data-testid="sidebar-logout">
            <LogOut size={16} /> <span className="sidebar-link-label">Déconnexion</span>
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile top bar with hamburger */}
      <div className="sidebar-mobile-bar">
        <button className="sidebar-hamburger" onClick={() => setOpen(true)} aria-label="Ouvrir le menu" data-testid="sidebar-open">
          <Menu size={22} />
        </button>
        <div className="sidebar-mobile-title">
          <img src={SMARTECH_LOGO_URL} alt="SMARTECH" className="sidebar-mobile-logo" />
          {title}
        </div>
        <div style={{ width: 38 }} />
      </div>

      {/* Desktop sidebar (always visible) */}
      <aside className="sidebar-desktop" data-testid="sidebar-desktop">{navBody}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="sidebar-backdrop" onClick={() => setOpen(false)}>
          <aside
            className="sidebar-drawer"
            onClick={(e) => e.stopPropagation()}
            data-testid="sidebar-drawer"
          >
            <button
              className="sidebar-close"
              onClick={() => setOpen(false)}
              aria-label="Fermer le menu"
              data-testid="sidebar-close"
            >
              <X size={20} />
            </button>
            {navBody}
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
